// src/pages/blog/BlogPostPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft } from 'lucide-react';

// --- IMPORT THE ACTUAL POSTS DATA ---
import { posts as allPostsData } from './postsData'; // Adjust path if needed

// --- UPDATED MOCK DATA FUNCTION ---
const getBlogPostBySlug = async (slug) => {
  // Simulate API call delay (optional, good for seeing loading state)
  // await new Promise(resolve => setTimeout(resolve, 100));
  return allPostsData[slug] || null;
};
// --- END OF DATA FUNCTION ---

const BlogPostPage = ({ onNavigate }) => {
  const { slug } = useParams();
  const navigateInternal = useNavigate();

  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [relatedPostsContent, setRelatedPostsContent] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchPost = async () => {
      setIsLoading(true);
      setError('');
      setPost(null); // Reset post state before fetching
      setRelatedPostsContent([]); // Reset related posts

      try {
        const fetchedPost = await getBlogPostBySlug(slug); // Uses the imported data
        if (fetchedPost) {
          setPost(fetchedPost);
          if (fetchedPost.relatedPosts && fetchedPost.relatedPosts.length > 0) {
            const related = await Promise.all(
              fetchedPost.relatedPosts.map(relatedSlug => getBlogPostBySlug(relatedSlug))
            );
            setRelatedPostsContent(related.filter(p => p));
          }
        } else {
          setError('Blog post not found. It might have been moved or deleted.');
        }
      } catch (e) {
        console.error("Error fetching blog post:", e);
        setError('Failed to load the blog post. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) { // Only fetch if slug is present
        fetchPost();
    } else {
        setError('No blog post specified.');
        setIsLoading(false);
    }

  }, [slug]);

  const handleInternalLinkClick = (e, path) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigateInternal(path);
    }
  };

  useEffect(() => {
    if (post?.title) {
      document.title = `${post.title} | TabDivider Blog`;
    } else if (error) {
        document.title = `Error | TabDivider Blog`;
    } else if (isLoading) {
        document.title = `Loading... | TabDivider Blog`;
    }
    return () => {
        document.title = 'TabDivider - Bill Splitting App';
    }
  }, [post, error, isLoading]);


  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12">
        <p className="text-xl text-gray-700 dark:text-gray-300">Loading article...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 text-center py-12">
        <p className="text-xl text-red-600 dark:text-red-400 mb-6">{error}</p>
        <button
          onClick={(e) => handleInternalLinkClick(e, '/blogs')}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Blog
        </button>
      </div>
    );
  }

  // This check should ideally be redundant if error handling is correct
  if (!post) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12">
            <p className="text-xl text-gray-700 dark:text-gray-300">Article data is unavailable.</p>
        </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 py-12 sm:py-16">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
            <button
                onClick={(e) => handleInternalLinkClick(e, '/blogs')}
                className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 group"
            >
                <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
                Back to Blog
            </button>
        </div>

        <header className="mb-8">
          {post.category && (
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
              {post.category}
            </span>
          )}
          <h1 className="mt-2 block text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-gray-900 dark:text-white">
            {post.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 gap-x-4 gap-y-2">
            {post.date && (
              <div className="flex items-center">
                <Calendar className="h-5 w-5 mr-1.5" />
                <span>{post.date}</span>
              </div>
            )}
            {post.author && (
              <div className="flex items-center">
                <User className="h-5 w-5 mr-1.5" />
                <span>By {post.author}</span>
              </div>
            )}
          </div>
          {post.imageUrl && (
            <img
              src={post.imageUrl}
              alt={`Featured image for ${post.title}`}
              className="w-full h-auto max-h-[500px] object-cover rounded-xl shadow-lg mt-8"
              loading="lazy"
            />
          )}
        </header>

        <div
          className="prose prose-lg sm:prose-xl dark:prose-invert max-w-none mx-auto"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {post.tags && post.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-full cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  // onClick={() => handleInternalLinkClick(null, `/blog/tag/${tag.toLowerCase().replace(/\s+/g, '-')}`)} // Example tag link
                >
                  <Tag className="h-3 w-3 mr-1 inline-block" /> {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {relatedPostsContent.length > 0 && (
            <section className="mt-16 pt-10 border-t border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                    Related Articles
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {relatedPostsContent.map(related => (
                        <div key={related.slug} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden p-5 flex flex-col">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
                                <a
                                    href={`/blog/${related.slug}`}
                                    onClick={(e) => handleInternalLinkClick(e, `/blog/${related.slug}`)}
                                    className="hover:text-blue-600 dark:hover:text-blue-400 line-clamp-2"
                                >
                                    {related.title}
                                </a>
                            </h3>
                            <div className="mt-auto pt-2">
                                <a
                                    href={`/blog/${related.slug}`}
                                    onClick={(e) => handleInternalLinkClick(e, `/blog/${related.slug}`)}
                                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    Read More →
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        )}
      </article>
    </div>
  );
};

export default BlogPostPage;